import React, { memo, useMemo } from 'react';
import { BaseEdge, EdgeLabelRenderer, Position, getBezierPath } from 'reactflow';

interface LoopEdgeProps {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourcePosition?: Position;
  targetPosition?: Position;
  id: string;
  data?: {
    isLoop?: boolean;
    loopType?: 'decision' | 'general' | 'data' | 'other';
  };
  style?: React.CSSProperties;
}

function LoopEdge({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  id,
  data,
  style,
}: LoopEdgeProps) {
  const adjustedTargetY = targetY - 5;

  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY: adjustedTargetY,
    targetPosition,
  });

  const [edgePath2] = getBezierPath({
    sourceX,
    sourceY: sourceY + 3,
    sourcePosition,
    targetX,
    targetY: adjustedTargetY + 3,
    targetPosition,
  });

  const isInLoop = data?.isLoop || false;
  const loopType = data?.loopType || 'general';

  const edgeStyles = useMemo(() => {
    if (isInLoop) {
      return {
        stroke: '#4ade80',
        strokeWidth: 1,
        strokeDasharray: 'none',
        fill: 'none',
      };
    }

    return {
      stroke: '#22c55e',
      strokeWidth: 1,
    };
  }, [isInLoop, loopType]);

  const gradientId = `loop-gradient-${id}`;
  const gradientColors = useMemo(() => {
    return { start: '#4ade80', middle: '#22c55e', end: '#16a34a' };
  }, []);

  return (
    <>
      {isInLoop && (
        <defs>
          <linearGradient id={gradientId} x1='0%' y1='0%' x2='100%' y2='0%'>
            <stop offset='0%' stopColor={gradientColors.start} />
            <stop offset='50%' stopColor={gradientColors.middle} />
            <stop offset='100%' stopColor={gradientColors.end} />
          </linearGradient>
        </defs>
      )}

      <BaseEdge
        path={edgePath}
        style={{
          ...style,
          ...edgeStyles,
        }}
      />

      {isInLoop && (
        <BaseEdge
          path={edgePath2}
          style={{
            ...style,
            stroke: '#4ade80',
            strokeWidth: 1,
            fill: 'none',
            strokeDasharray: 'none',
          }}
        />
      )}

      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            left: (sourceX + targetX) / 2,
            top: (sourceY + adjustedTargetY) / 2,
            fontSize: '10px',
            fontWeight: 'bold',
            color: '#ffffff',
            backgroundColor: '#4ade80',
            padding: '3px 6px',
            borderRadius: '4px',
            border: '2px solid #22c55e',
            whiteSpace: 'nowrap',
            zIndex: 1000,
            transform: 'translate(-50%, -50%)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
            pointerEvents: 'none',
          }}
        >
          LOOP
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

export default memo(LoopEdge); 